import type { FC, Ref } from "react";

import { INDEX_NOT_FOUND } from "components/constants";
import { InteractionModeIconText } from "components/materials/page/interaction-mode/InteractionModeIconText";
import { INTERACTION_MODE_CHIP_KINDS } from "components/materials/page/interaction-mode/helpers";
import { Chip } from "mds/components/Chip";
import { pageSelectors } from "store/selectors";
import { useObjectSelector } from "store/store-hooks";
import { isGroupPage } from "utils/page-utils";

export type InteractionModeChipProps = {
  pageId: string;
  pageGroupIndex?: number;
  disabled?: boolean;
  ref?: Ref<HTMLButtonElement>;
  onGroupCardToggle?: () => void;
  hasTooltip?: boolean;
};

export const InteractionModeChip: FC<InteractionModeChipProps> = ({
  pageId,
  pageGroupIndex = INDEX_NOT_FOUND,
  disabled = false,
  ref,
  onGroupCardToggle,
  hasTooltip = false,
}) => {
  const page = useObjectSelector((s) => pageSelectors.selectById(s, pageId), ["interaction_mode"]);

  if (!page) {
    return null;
  }

  const iconText = <InteractionModeIconText pageGroupIndex={pageGroupIndex} pageId={pageId} />;
  const isGroupToggle = !!onGroupCardToggle && isGroupPage(page);

  if (isGroupToggle) {
    return (
      <Chip
        as="button"
        eventName="Group Settings - Toggle"
        kind="hibiscus"
        ref={ref}
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
          onGroupCardToggle();
        }}
      >
        {iconText}
      </Chip>
    );
  }

  if (hasTooltip || disabled) {
    return (
      <Chip
        aria-disabled={disabled || undefined}
        as="button"
        kind={INTERACTION_MODE_CHIP_KINDS[page.interaction_mode]}
        ref={ref}
      >
        {iconText}
      </Chip>
    );
  }

  return <Chip kind={INTERACTION_MODE_CHIP_KINDS[page.interaction_mode]}>{iconText}</Chip>;
};

InteractionModeChip.displayName = "InteractionModeChip";
